import Image from 'next/image';
import Link from 'next/link';
import { components } from '@/types/strapi';

type DynamicZoneLogoGridComponent =
	components['schemas']['DynamicZoneLogoGridComponent'];

interface LogoItemProps {
	item: components['schemas']['Logo'];
	imageSize: { width: number; height: number; maxHeight: string };
	cardHeight: string;
	variant: components['schemas']['DynamicZoneLogoGridComponent']['variant'];
}
interface LogoGridProps extends DynamicZoneLogoGridComponent {
	logos: components['schemas']['Logo'][];
	className?: string;
}

const variants = {
	showcase: {
		wrapper: 'bg-white',
		imageSize: { width: 150, height: 150, maxHeight: 'max-h-32' },
		cardHeight: 'h-40',
	},
	strip: {
		wrapper: 'bg-gray-50',
		imageSize: { width: 120, height: 45, maxHeight: 'max-h-10' },
		cardHeight: 'h-16',
	},
	compact: {
		wrapper: 'bg-mtalkz-50',
		imageSize: { width: 150, height: 60, maxHeight: 'max-h-16' },
		cardHeight: 'h-32',
	},
} as const;

type VariantType = keyof typeof variants;

export const LogoGrid = ({
	heading,
	sub_heading,
	logos,
	variant = 'showcase',
	className = '',
}: LogoGridProps) => {
	// Ensure variant is valid, fallback to 'showcase' if not
	const safeVariant = (
		variant in variants ? variant : 'showcase'
	) as VariantType;
	const styles = variants[safeVariant];

	// We need at least 2 copies to ensure smooth scrolling
	const doubledLogos = [...logos, ...logos];

	return (
		<section
			className={`py-6 ${styles.wrapper} ${className}`}
			aria-labelledby="section-heading">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				{heading && (
					<h2 className="text-mtalkz-800 mb-6 text-center text-3xl font-bold">
						{heading}
					</h2>
				)}
				{sub_heading && (
					<p className="mb-4 text-center">{sub_heading}</p>
				)}

				{/* Simple marquee wrapper */}
				<div
					className={`${safeVariant === 'strip' ? 'py-2' : 'py-4'} marquee-wrapper`}>
					<div
						className={`marquee ${safeVariant === 'strip' ? 'flex flex-wrap justify-center gap-3' : ''}`}>
						{doubledLogos.map((item, index) => (
							<div
								key={`logo-${item.id}-${index}`}
								className={`marquee-item`}>
								<LogoItem
									item={item}
									imageSize={styles.imageSize}
									cardHeight={styles.cardHeight}
									variant={safeVariant}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

const LogoItem = ({ item, imageSize, variant }: LogoItemProps) => {
	const ImageComponent = (
		<Image
			src={item.image.url || ''}
			alt={item.name}
			width={imageSize.width}
			height={imageSize.height}
			className={`h-auto w-auto ${imageSize.maxHeight} object-contain transition-transform duration-300 hover:scale-110`}
		/>
	);

	if (variant === 'strip') {
		return (
			<Link
				href={item.url || '#'}
				target="_blank"
				rel="noopener noreferrer"
				className="flex h-[56px] w-[160px] min-w-[160px] items-center justify-center rounded border border-gray-100 bg-white px-3 py-2 shadow-sm transition-all hover:border-blue-100 hover:shadow-md">
				{ImageComponent}
			</Link>
		);
	}

	return (
		<div
			className={`${variant === 'showcase' ? 'bg-transparent' : 'bg-white'} group overflow-hidden transition-all ${variant !== 'showcase' && 'hover:shadow-lg'}`}>
			<div className={`flex items-center justify-center p-2`}>
				{item.url ? (
					<a
						href={item.url}
						target="_blank"
						rel="noopener noreferrer"
						className="block">
						{ImageComponent}
					</a>
				) : (
					ImageComponent
				)}
			</div>
		</div>
	);
};
